import { useGetSocialMediaSearch } from "@entities/influencer/lib/hooks/useGetSocialMediaSearch";
import { SocialMedia, SocialMediaSearch } from "@shared/lib/types/influencer";
import { Avatar } from "antd";
import { Instagram, Loader2, User, Youtube } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import styles from "../styles.module.scss";

type Platform = "instagram" | "youtube";

export interface AddCreatorBarProps {
  disabled?: boolean;
  onSubmit: (creator: Partial<SocialMedia> & Partial<SocialMediaSearch>) => void;
}

export const AddCreatorBar: React.FC<AddCreatorBarProps> = ({ disabled, onSubmit }) => {
  const [platform, setPlatform] = useState<Platform>("instagram");
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const [debouncedValue] = useDebounce(value, 500);

  const { searchedInfluencers, isFetching } = useGetSocialMediaSearch(debouncedValue, platform);

  const suggestions = searchedInfluencers || [];

  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => setHighlight(0), [debouncedValue, platform]);

  const commit = (suggestion: Partial<SocialMedia> & Partial<SocialMediaSearch>) => {
    if (disabled) return;
    onSubmit({ ...suggestion, platform });
    setValue("");
    setOpen(false);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div ref={wrapRef} className={styles.addCreatorBarWrap}>
      <div className={styles.addCreatorInputs}>
        <div className={styles.platformToggle}>
          {(
            [
              { value: "instagram" as const, label: "IG" },
              { value: "youtube" as const, label: "YT" },
            ] as const
          ).map((p) => {
            const active = platform === p.value;
            return (
              <button
                key={p.value}
                type="button"
                disabled={disabled}
                onClick={() => setPlatform(p.value)}
                className={`${styles.platformBtn} ${active ? styles.platformBtnActive : ""}`}
              >
                {p.value === "youtube" ? (
                  <Youtube size={14} className={styles.platformIconYoutube} />
                ) : (
                  <Instagram size={14} className={styles.platformIconInstagram} />
                )}
                <span>{p.label}</span>
              </button>
            );
          })}
        </div>

        <div className={styles.usernameInputWrap}>
          <span className={styles.atSymbol}>@</span>
          <input
            ref={inputRef}
            value={value.replace(/^@+/, "")}
            disabled={disabled}
            onChange={(e) => {
              setValue(e.target.value.replace(/^@+/, ""));
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" && suggestions.length) {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
                return;
              }
              if (e.key === "ArrowUp" && suggestions.length) {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
                return;
              }
              if (e.key === "Escape") {
                setOpen(false);
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                if (open && suggestions[highlight]) {
                  commit(suggestions[highlight]);
                }
              }
            }}
            placeholder="username — Enter to analyze"
            autoComplete="off"
            className={styles.usernameInput}
          />
          {isFetching && <Loader2 size={16} className={`spin-animation ${styles.inputLoader}`} />}
        </div>
      </div>

      {open && (value.length > 0 || isFetching) && !disabled && (
        <div className={styles.suggestionsDropdown}>
          <div className={styles.suggestionsHeader}>Existing creators</div>
          <ul className={styles.suggestionsList}>
            {isFetching && suggestions.length === 0 && (
              <li className={styles.suggestionsLoading}>Searching...</li>
            )}
            {!isFetching && suggestions.length === 0 && value.length > 0 && (
              <li className={styles.suggestionsLoading}>No creators found.</li>
            )}
            {suggestions.map((s: Partial<SocialMedia> & Partial<SocialMediaSearch>, i: number) => (
              <li key={`${s.platform}-${s.username}`}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(s)}
                  className={`${styles.suggestionBtn} ${i === highlight ? styles.suggestionBtnActive : ""}`}
                >
                  <Avatar
                    src={s.profilePicUrl ? s.profilePicUrl : undefined}
                    icon={s.profilePicUrl ? undefined : <User size={14} />}
                    className={styles.suggestionImg}
                  />
                  <div className={styles.suggestionInfo}>
                    <div className={styles.suggestionName}>{s.username}</div>
                    {s.isLinkedToInfluencer && (
                      <div className={styles.suggestionHandle}>Linked to: {s.linkedInfluencer?.firstName}</div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
